import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Files, FileCheck2, FileClock, Loader2, ArrowRight, Coins } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import { getCompanies, listComputations, getMe } from "../lib/api";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const filingBadge = (s) => {
  const v = (s || "Draft");
  if (v === "Filed") return "bg-green-100 text-green-700";
  if (v === "Pending") return "bg-amber-100 text-amber-700";
  return "bg-gray-100 text-gray-600";
};

const StatCard = ({ icon: Icon, iconBg, label, value, caption, testid }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm transition-shadow hover:shadow-md" data-testid={testid}>
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white ${iconBg}`}>
        <Icon size={20} />
      </div>
      <span className="text-sm font-medium text-gray-700">{label}</span>
    </div>
    <div className="flex items-end justify-between gap-2">
      <span className="text-3xl font-bold text-gray-900 tracking-tight">{value}</span>
      <span className="text-xs text-gray-400 text-right leading-tight max-w-[55%]">{caption}</span>
    </div>
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());
  const [year, setYear] = useState(null);
  const [me, setMe] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const cos = await getCompanies();
      setCompanies(cos || []);
      const ids = new Set((cos || []).map((c) => c.id));
      const all = await listComputations();
      setComps((all || []).filter((r) => ids.has(r.companyId)));
      try { setMe(await getMe()); } catch (e) { /* ignore */ }
    } catch (e) {
      setCompanies([]);
      setComps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Stats
  const totalTaxFiles = companies.length;
  // A company is "submitted/completed" when it has at least one locked (submitted) computation.
  const submittedCompanyIds = useMemo(
    () => new Set(comps.filter((r) => r.locked).map((r) => r.companyId)),
    [comps]
  );
  const submitted = useMemo(
    () => companies.filter((c) => submittedCompanyIds.has(c.id) || c.filingStatus === "Filed" || c.filingStatus === "Submitted").length,
    [companies, submittedCompanyIds]
  );
  // Pending = has a saved computation but not yet submitted.
  const withComputation = useMemo(() => {
    const withComp = new Set(comps.map((r) => r.companyId));
    return companies.filter((c) => withComp.has(c.id) && !submittedCompanyIds.has(c.id)).length;
  }, [companies, comps, submittedCompanyIds]);

  // Years available from computations
  const years = useMemo(() => {
    const set = new Set();
    comps.forEach((r) => { const d = new Date(r.createdAt); if (!isNaN(d)) set.add(d.getFullYear()); });
    const arr = Array.from(set).sort((a, b) => b - a);
    return arr.length ? arr : [new Date().getFullYear()];
  }, [comps]);

  useEffect(() => {
    if (year === null && years.length) setYear(years[0]);
  }, [years, year]);

  const chartData = useMemo(() => {
    const counts = new Array(12).fill(0);
    comps.forEach((r) => {
      const d = new Date(r.createdAt);
      if (!isNaN(d) && d.getFullYear() === year) counts[d.getMonth()] += 1;
    });
    return MONTHS.map((m, i) => ({ month: m, files: counts[i] }));
  }, [comps, year]);

  const recent = useMemo(() => {
    const byId = Object.fromEntries(companies.map((c) => [c.id, c]));
    return [...comps]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 7)
      .map((r) => ({ ...r, company: byId[r.companyId] }));
  }, [comps, companies]);

  const dateStr = now.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const dayTime = now.toLocaleDateString("en-US", { weekday: "long" }) + ", " +
    now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });

  return (
    <div className="max-w-[1400px] mx-auto">
      {/* Greeting */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">
          Hello, <span className="text-[#16b981]" data-testid="dash-greeting-name">{me?.name || user?.name || user?.email || "there"}</span>
        </h1>
        <p className="text-sm text-gray-500 mt-1">{dateStr} <span className="text-gray-300">|</span> {dayTime}</p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Files} iconBg="bg-slate-800" label="Total Tax Files" value={loading ? "—" : totalTaxFiles} caption="Total company tax files" testid="stat-total-files" />
        <StatCard icon={FileCheck2} iconBg="bg-[#16b981]" label="Completed File" value={loading ? "—" : submitted} caption="Submitted tax files" testid="stat-completed-files" />
        <StatCard icon={FileClock} iconBg="bg-amber-500" label="Pending File" value={loading ? "—" : withComputation} caption="Tax files with a saved computation" testid="stat-pending-files" />
        <StatCard
          icon={Coins}
          iconBg="bg-[#1877c9]"
          label="Available Credits"
          value={loading || !me ? "—" : (me.isOwner ? "Unlimited" : me.credits)}
          caption={me?.isOwner ? "Owner — unlimited tax files" : "1 credit = 1 new tax file"}
          testid="stat-credits"
        />
      </div>

      {/* Chart + Recent */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Chart */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm" data-testid="dash-chart-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-gray-800">Tax Files</h2>
            <Select value={year != null ? String(year) : ""} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="h-8 w-24 text-sm" data-testid="dash-chart-year">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f6" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13 }} />
                <Bar dataKey="files" radius={[6, 6, 0, 0]} maxBarSize={34}>
                  {chartData.map((d, i) => (
                    <Cell key={i} fill={i % 2 === 0 ? "#16b981" : "#8fe3c4"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Recent Tax Computations */}
        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm" data-testid="dash-recent-card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-gray-800">Recent Tax Computations</h2>
            <button onClick={() => navigate("/company")} className="text-sm text-[#1877c9] hover:underline inline-flex items-center gap-1" data-testid="dash-see-all">
              See All <ArrowRight size={14} />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 text-xs border-b border-gray-100">
                  <th className="py-2 pr-3 font-medium">Company</th>
                  <th className="py-2 pr-3 font-medium">YA</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => r.company && navigate(`/company/${r.companyId}/tax?load=${r.id}`)}
                    className="border-b border-gray-50 hover:bg-[#f9fbfe] cursor-pointer"
                    data-testid={`dash-recent-row-${r.id}`}
                  >
                    <td className="py-2.5 pr-3 font-medium text-gray-800 max-w-[180px] truncate">{r.companyName || r.company?.name || "—"}</td>
                    <td className="py-2.5 pr-3 text-gray-600">{r.ya}</td>
                    <td className="py-2.5 pr-3">
                      {r.locked ? (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">
                          Submitted
                        </span>
                      ) : (
                        <span className={`px-2 py-0.5 rounded-full text-xs ${filingBadge(r.company?.filingStatus)}`}>
                          {r.company?.filingStatus || "Draft"}
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 text-gray-500 whitespace-nowrap">
                      {new Date(r.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                    </td>
                  </tr>
                ))}
                {loading && (
                  <tr><td colSpan={4} className="py-10 text-center text-gray-400"><Loader2 className="inline animate-spin mr-2" size={16} />Loading…</td></tr>
                )}
                {!loading && recent.length === 0 && (
                  <tr><td colSpan={4} className="py-10 text-center text-gray-400">No tax computations yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
